<template>
  <div
    class="product-recommendations-element critical-component-clear"
    :class="{
      [$class('hidden')]: hidden,
      'non-dy-recommendations': productsSource === 'manual'
        || productsSource === 'shopify_native_rec',
      'no-shopify-recommendations': productsSource === 'shopify_native_rec'
        && products.length === 0,
    }"
  >
    <dynamic-yield-template
      v-if="productsSource === 'dynamic_yield' && settings.dynamicYieldTarget !== ''"
      :target="settings.dynamicYieldTarget"
      :settings="settings"
    />

    <product-carousel
      v-else-if="productsSource === 'manual'
        || productsSource === 'shopify_native_rec'
        || !dyCampaign
        || targetProducts.length > 0"
      :loading="productsSource === 'dynamic_yield'"
      :products="productsSource === 'shopify_native_rec' ? products : localProducts"
      :settings="settings"
      custom-report
      :intent="intent"
    />
  </div>
</template>

<script>

/**
 * Vue: Product recommendations (product-recommendations)
 * -----------------------------------------------------------------------------
 * Product recommendations section.
 *
 * @param {Array} carouselProducts - Products to be displayed in carousel.
 * @param {String} productsSource - Source of products in section.
 * @param {Object} settings - Object containing section settings.
 * @param {Object} targetProducts - Array of products from chosen collection.
 */
import { mapState } from 'vuex'

import DynamicYieldTemplate from '~async/dynamic-yield-template/dynamic-yield-template'
import ProductCarousel from '~global/product-carousel/product-carousel'
import { shopifyProductsByIdQuery } from '~/helpers/shopify'

import { getLoadingProducts } from '~/helpers/dynamic-yield'

export default {
  name: 'ProductRecommendations',
  components: {
    DynamicYieldTemplate,
    ProductCarousel,
  },

  props: {
    carouselProducts: {
      type: Array,
      default: () => [],
    },
    productsSource: {
      type: String,
      default: 'manual',
    },
    targetProducts: {
      type: Array,
      default: () => [],
    },
    settings: {
      type: Object,
      default: () => ({
        id: '',
        cardBackground: '--color-brand-white',
        cardLayout: 'regular',
        columnCount: 'small',
        dynamicYieldTarget: '',
        showBadges: false,
        showCarouselArrows: true,
        showFinalSale: false,
        showPreorderBadge: false,
        showSecondaryName: false,
        showWishlist: false,
        title: '',
        titleAlignment: 'default',
        titleClass: '',
        paddedImages: false,
        finalImageDesktop: null,
        finalImageMobile: null,
        finalImageText: '',
        finalImageUrl: null,
        finalImageOverlay: false,
        showFinalImage: false,
      }),
    },
    productId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      hidden: false,
      intent: '',
      products: [],
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      dyCampaigns: (state) => state['dynamic-yield'].campaigns,
      loadedCampaigns: (state) => state['dynamic-yield'].loaded,
    }),

    /**
     * The DY campaign for this section, including Shopify section data.
     * @returns {Object} The DY campaign data with Shopify section data.
     */
    dyCampaign() {
      const dyCampaign = this.dyCampaigns[this.settings.dynamicYieldTarget] || null

      if (!dyCampaign) {
        return null
      }

      return {
        ...dyCampaign,
        section: this.settings,
      }
    },

    /**
     * The DY loaded state for this campaign.
     * @returns {Boolean}
     */
    loaded() {
      return this.loadedCampaigns[this.settings.dynamicYieldTarget] || false
    },

    /**
     * Compute local products.
     * - Used to render loading products for Dynamic Yield or the carousel
     *   products, returns false if neither conditions are met.
     * @returns {Boolean|Array}
     */
    localProducts() {
      if (this.productsSource === 'dynamic_yield' && this.settings.dynamicYieldTarget !== '') {
        return getLoadingProducts()
      }

      if (this.carouselProducts?.length) {
        return this.carouselProducts
      }

      if (
        (this.carouselProducts?.length === 0 || this.settings.dynamicYieldTarget === '') &&
        this.targetProducts.length > 0 && window.cnvs.page.type !== 'product'
      ) {
        return this.targetProducts
      }

      return false
    },
  },

  watch: {

    /**
     * Watch loaded state to hide section if no campaign data is returned.
     */
    loaded: {
      handler() {
        this.setHidden()
      },
      immediate: true,
    },

    /**
     * Watch campaign state to show section when loaded
     */
    dyCampaign: {
      handler() {
        this.setHidden()
      },
      immediate: true,
    },

    productsSource: {
      handler() {
        this.fetchSource()
      },
      immediate: true,
    },
  },

  methods: {
    setHidden() {
      if (this.productsSource === 'dynamic_yield' &&
        this.settings.dynamicYieldTarget === '' &&
        window.cnvs.page.type === 'product'
      ) {
        this.hidden = true
        return
      }

      if (this.productsSource === 'manual' &&
        this.carouselProducts.length === 0 &&
        window.cnvs.page.type === 'product'
      ) {
        this.hidden = true
        return
      }

      if (!this.loaded && !this.dyCampaign) {
        return
      }

      if (this.productsSource === 'shopify_native_rec' && window.cnvs.page === 'product') {
        return false
      }

      if (
        this.productsSource === 'manual' &&
        this.carouselProducts?.length === 0 &&
        window.cnvs.page.type === 'product'
      ) {
        return true
      }

      if (
        this.dyCampaign ||
        this.productsSource !== 'dynamic_yield'
      ) {
        this.hidden = false
        return
      }

      const section = document.querySelector(`[data-section-id="${this.settings.id}"]`)

      if (!section) {
        return
      }

      this.hidden = true
    },

    /**
     * Checks source of products
     */
    async fetchSource() {
      if (this.productsSource === 'shopify_native_rec' && window.cnvs.page.type === 'product') {
        await this.fetchShopifyRecommendations()
      } else {
        this.products = this.targetProducts.length > 0 ? this.targetProducts : []
      }
    },

    /**
     * Fetch recommendation via Shopify recs API
     */
    async fetchShopifyRecommendations() {
      try {
        const response = await fetch(`${cnvs.routes.productRecommendations}?product_id=${this.productId}`)
        const data = await response.json()
        if (data.products.length > 0) {
          this.intent = data.intent
          this.queryProducts(data.products)
        }
      } catch (error) {
        cnvs.ReportError('Failed to load products', error)
      }
    },

    /**
     * Request recommendation products via ID
     * @param productArray
     */
    async queryProducts(productArray) {
      const ids = productArray.map((product) => `gid://shopify/Product/${product.id}`)
      const results = await shopifyProductsByIdQuery(ids, false)
      this.products = results || []
    },
  },
}
</script>

<style lang="scss">
@import './product-recommendations';
</style>
